import React from "react";
import {
  Button,
  Checkbox,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import {
  ControlledCustomSelect,
  CustomFormLabel,
} from "../../../../../../../components/formsComponents";
import { LabelCard } from "../../../../../../../components/LabelCard/LabelCard";
import { StandardTableActions } from "../../../../../../../components/StandardTableActions/StandardTableActions";
import produce from "immer";
import { IBusinessPolicyChangeStepsProps } from "../PolicyChange.types";
import { ConfirmDialog } from "../../../../../../../components/Dialogs/ConfirmDialog";
import { AddOutlined } from "@mui/icons-material";
import { IBusinessPolicyFineArt } from "../../../../../../../redux/businessPolicy/businessPolicyFineArt/businessPolicyFineArt.types";
import { FineArtRightPanel } from "../../FineArt/FineArtRightPanel";
import { useBusinessPolicyFineArt } from "../../hooks/useBusinessPolicyFineArt";

export const FineArtChange: React.FC<IBusinessPolicyChangeStepsProps> = ({
  values,
  setValues,
  errors,
  handleChange,
  setFieldValue,
  customerId,
  customerPolicyId,
  isStatusCompleted,
  autoSaveData = () => {},
}) => {
  const [open, setOpen] = React.useState<{
	type: "create" | "edit" | "view";
	index?: number;
	data?: IBusinessPolicyFineArt;
  } | null>(null);
  const theme = useTheme();
  const { fineArtDropdown, refreshData } =
	useBusinessPolicyFineArt(customerPolicyId);

  const handleCheckBoxChange = (value: string) => {
	setFieldValue("fine_art_change.type", value);
  };

  const handleSaveSuccess = (fineArt: IBusinessPolicyFineArt) => {
	const newValues = produce(values, (draftValues) => {
	  const updatedData = {
		...fineArt,
		customer_policy_id: customerPolicyId ?? null,
		customer_id: customerId ? Number(customerId) : null,
	  };
	  if (open && open.type === "edit" && open.index !== undefined) {
		draftValues.fine_art_change.records[open.index] = updatedData;
	  } else {
		draftValues.fine_art_change.records.push(updatedData);
	  }
	});
	autoSaveData(newValues);
	setValues(newValues);
	setOpen(null);
	refreshData();
  };

  const handleCreateEdit = (index?: number) => () => {
	if (isStatusCompleted) {
	  return;
	}
	setOpen({
	  type: index !== undefined ? "edit" : "create",
	  index: index,
	  data:
		index !== undefined
		  ? values.fine_art_change.records[index]
		  : undefined,
	});
  };

  const handleView = (index: number) => () => {
	setOpen({
	  type: "view",
	  index: index,
	  data: values.fine_art_change.records[index],
	});
  };

  const [deleteConfirm, setDeleteConfirm] = React.useState<number | null>(null);
  const handleDelete = (index: number) => () => {
	setDeleteConfirm(index);
  };

  const handleDeleteConfirm = () => {
	if (deleteConfirm === null) {
	  return;
	}
	const newValues = produce(values, (draftValues) => {
	  draftValues.fine_art_change.records.splice(deleteConfirm, 1);
	});
	setValues(newValues);
	autoSaveData(newValues);
	setDeleteConfirm(null);
  };

  return (
	<>
	  <LabelCard heading="Fine Art Change" />
	  <Grid container spacing={2} mb={3}>
		<Grid item xs={12} lg={2.4}>
		  <Stack direction={"row"} alignItems={"center"}>
			<Checkbox
			  checked={values.fine_art_change.type === "Add"}
			  onChange={(e) => {
				handleCheckBoxChange(e.target.checked ? "Add" : "");
			  }}
			/>
			<Typography>Add</Typography>
		  </Stack>
		</Grid>
		<Grid item xs={12} lg={2.4}>
		  <Stack direction={"row"} alignItems={"center"}>
			<Checkbox
			  checked={values.fine_art_change.type === "Delete"}
			  onChange={(e) => {
				handleCheckBoxChange(e.target.checked ? "Delete" : "");
			  }}
			/>
			<Typography>Delete</Typography>
		  </Stack>
		</Grid>
		<Grid item xs={12} lg={2.4}>
		  <Stack direction={"row"} alignItems={"center"}>
			<Checkbox
			  checked={values.fine_art_change.type === "Change"}
			  onChange={(e) => {
				handleCheckBoxChange(e.target.checked ? "Change" : "");
			  }}
			/>
			<Typography>Substitution</Typography>
		  </Stack>
		</Grid>
	  </Grid>

	  {values.fine_art_change.type &&
		["Add", "Change"].includes(values.fine_art_change.type) && (
		  <>
			<Grid container spacing={3}>
			  {values.fine_art_change.type === "Change" && (
				<Grid item xs={12} md={6}>
				  <CustomFormLabel>Delete Fine Art</CustomFormLabel>
				  <ControlledCustomSelect
					variant="outlined"
					size="small"
					type="string"
					name="fine_art_change.delete_fine_art"
					fullWidth
					displayEmpty
					value={Number(values.fine_art_change.delete_fine_art)}
					onChange={handleChange}
					placeholder="Select One"
					options={fineArtDropdown}
				  ></ControlledCustomSelect>
				</Grid>
			  )}
			  <Grid item xs={12}>
				<Stack direction={"row"} justifyContent={"end"}>
				  <Button
					variant="contained"
					disabled={isStatusCompleted}
					onClick={handleCreateEdit()}
					startIcon={<AddOutlined />}
				  >
					New
				  </Button>
				</Stack>
				<TableContainer>
				  <Table>
					<TableHead>
					  <TableRow>
						<TableCell>View</TableCell>
						<TableCell>Location #</TableCell>
						<TableCell>Effective Date</TableCell>
						<TableCell>Serial Number</TableCell>
						<TableCell>Limit Amount</TableCell>
					  </TableRow>
					</TableHead>
					<TableBody>
					  {values.fine_art_change.records.map((item, index) => {
						return (
						  <TableRow key={index}>
							<TableCell sx={{ display: "flex" }}>
							  <StandardTableActions
								onViewClick={handleView(index)}
								onEditClick={
								  !isStatusCompleted
									? handleCreateEdit(index)
									: undefined
								}
								// onDeleteClick={
								//   !isStatusCompleted
								// 	? handleDelete(index)
								// 	: undefined
								// }
							  />
							</TableCell>
							<TableCell>{index + 1}</TableCell>
							<TableCell>{item.effective_date}</TableCell>
							<TableCell>{item.serial_number}</TableCell>
							<TableCell>{item.limit_amount}</TableCell>
						  </TableRow>
						);
					  })}
					</TableBody>
				  </Table>
				</TableContainer>
			  </Grid>
			</Grid>
		  </>	
		)}

	  {values.fine_art_change.type &&
		["Delete"].includes(values.fine_art_change.type) && (
		  <Grid container>
			<Grid item xs={12} md={6}>
			  <CustomFormLabel>Delete Fine Art</CustomFormLabel>
			  <ControlledCustomSelect
				variant="outlined"
				size="small"
				type="string"
				name="fine_art_change.delete_fine_art"
				fullWidth
				displayEmpty
				value={Number(values.fine_art_change.delete_fine_art)}
				onChange={handleChange}
				placeholder="Select One"
				options={fineArtDropdown}
			  ></ControlledCustomSelect>
			</Grid>
		  </Grid>
		)}

	  {open && (
		<FineArtRightPanel
		  open={true}
		  readonly={open.type === "view"}
		  customerId={customerId}
		  customerPolicyId={customerPolicyId}
		  fineArtNumber={open.data ? open.data.incremental_number as any : undefined}
		  onSaveSuccess={handleSaveSuccess}
		  //staticStatusValue="INACTIVE"
		  created_from="Policy_Change"
		  onClose={() => setOpen(null)}
		  //initialData={open.data}
		 // disableApiCalls
		/>
	  )}
	  {deleteConfirm !== null && (
		<ConfirmDialog
		  open
		  content="Are you sure you want to delete this record?"
		  onClose={() => setDeleteConfirm(null)}
		  onConfrim={handleDeleteConfirm}
		/>
	  )}
	</>
  );
};
